const CTA_STATES = {
    EXTERNAL: 'external',
    WAITLIST: 'waitlist',
    DOWNLOADABLE: 'downloadable',
    HIDDEN: 'hidden',
    NORMAL: 'normal'
}

const CTA_TEXT_DEFAULTS = {
    normal: 'Add to Cart',
    soldOut: 'Sold Out',
    waitlist: 'Waitlist',
    downloadable: 'Download'
}

// Single source of truth for the product CTA state + text, shared by the
// PDP (ProductTier, variant-level availability) and cards (ProductThumb,
// product-level availability). All metafield-driven options are optional
// and degrade to the plain available/'Sold Out' behavior when absent
// (e.g. predictive search results, which have no metafields).
// ctaHideIfSoldout is PDP-only: it hides the buy button on the product page,
// while ProductThumb opts out (forces it false) to keep its sold-out label.
// `state` follows the ProductTier template branch order.
const getCtaState = ({
    available = true,
    ctaTextOverride = '',
    ctaUrlOverride = '',
    ctaTextOverrideAlways = false,
    ctaHideIfSoldout = false,
    downloadableType = '',
    waitlistId = ''
} = {}) => {
    const isExternalCta = !!ctaUrlOverride
    const isWaitlist = !!waitlistId && !available && !isExternalCta
    const isDownloadable = !!downloadableType && !isExternalCta
    const isHideCta = ctaHideIfSoldout && !available && !isWaitlist && !isDownloadable

    const isCtaTextOverrideAlways = !!(ctaTextOverrideAlways && ctaTextOverride)
    const ctaTextNormal = ctaTextOverride || CTA_TEXT_DEFAULTS.normal
    const ctaTextSoldOut = (isCtaTextOverrideAlways ? ctaTextOverride : CTA_TEXT_DEFAULTS.soldOut) || CTA_TEXT_DEFAULTS.soldOut
    // the override text always wins on the waitlist button, no override-always flag needed
    const ctaTextWaitlist = ctaTextOverride || CTA_TEXT_DEFAULTS.waitlist
    const ctaTextDownloadable = (isCtaTextOverrideAlways ? ctaTextOverride : CTA_TEXT_DEFAULTS.downloadable) || CTA_TEXT_DEFAULTS.downloadable

    let state = CTA_STATES.NORMAL
    if (isExternalCta) state = CTA_STATES.EXTERNAL
    else if (isWaitlist) state = CTA_STATES.WAITLIST
    else if (isDownloadable) state = CTA_STATES.DOWNLOADABLE
    else if (isHideCta) state = CTA_STATES.HIDDEN

    const ctaText = {
        [CTA_STATES.EXTERNAL]: ctaTextNormal,
        [CTA_STATES.WAITLIST]: ctaTextWaitlist,
        [CTA_STATES.DOWNLOADABLE]: ctaTextDownloadable,
        [CTA_STATES.HIDDEN]: '',
        [CTA_STATES.NORMAL]: available ? ctaTextNormal : ctaTextSoldOut
    }[state]

    return {
        state,
        isExternalCta,
        isWaitlist,
        isDownloadable,
        isHideCta,
        ctaTextNormal,
        ctaTextSoldOut,
        ctaTextWaitlist,
        ctaTextDownloadable,
        ctaText
    }
}

// Maps the snake_case JSON emitted by json_product.liquid / json_collection.liquid
// into getCtaState() options. Safe to call with null/undefined.
const ctaOptionsFromJson = (json) => ({
    ctaTextOverride: json?.cta_text_override || '',
    ctaUrlOverride: json?.cta_url_override || '',
    ctaTextOverrideAlways: json?.cta_text_override_always || false,
    ctaHideIfSoldout: json?.cta_hide_if_soldout || false,
    downloadableType: json?.downloadable_type || '',
    waitlistId: json?.waitlist_id || ''
})

// Status label for product cards (ProductThumb): '' means "show the price".
// The collection-level products_to_not_show_as_soldout metafield suppresses
// the default sold-out label so gift-with-purchase cards read "Free"/price,
// but a product that explicitly sets cta_text_override still gets its
// sold-out label — that's how a retired promo card flips from "Free" to
// "Sold Out" without editing the collection metafield.
// cta_hide_if_soldout is PDP-only, so the card always opts out of it.
const getCardStatusText = ({
    available = true,
    isNotShowAsSoldOut = false,
    cta = null
} = {}) => {
    if (available !== false) return ''
    if (isNotShowAsSoldOut && !cta?.cta_text_override) return ''

    return getCtaState({
        available: false,
        ...ctaOptionsFromJson(cta),
        ctaHideIfSoldout: false
    }).ctaText
}

export {
    CTA_STATES,
    getCtaState,
    getCardStatusText,
    ctaOptionsFromJson
}
